import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useUser } from '@/contexts/UserContext';
import { useHeader } from '@/contexts/HeaderContext';
import { useStats } from '@/contexts/StatsContext';
import { supabase } from '@/lib/supabase';
import { tileRenderers, setPlantsImage } from '@/components/experimental/IsometricGardenPrototype';
import { countTiles } from '@/components/experimental/isometricGardenModel';
import plantsImgUrl from '@/res/tiles/craftpix-net-200380-free-pixel-art-plants-for-farm/PNG/Plants.png';
import axios from 'axios';
import Loading from '../components/Loading';
import StatsSkeleton from '../components/skeletons/StatsSkeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Week day labels ─── */
const WEEKDAYS_EN = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const WEEKDAYS_IT = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];
const WEEKDAYS_ES = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

/* ─── Category colors (Forest-inspired warm palette) ─── */
const CATEGORY_COLORS = [
  '#E8B87E', // warm gold
  '#D4917A', // salmon
  '#A8C5A0', // sage green
  '#B8A9C9', // lavender
  '#7CB8C9', // teal
  '#E8A0B4', // rose
  '#C4B58E', // khaki
  '#9BAFA3', // muted green
  '#D4A574', // tan
  '#8FA8C8', // steel blue
];

/* ─── Category config ─── */
const CATEGORY_CONFIG = {
  repair:       { en: 'Repair',       it: 'Riparazione',   icon: 'solar:shield-check-linear', color: '#D4917A' },
  connection:   { en: 'Connection',   it: 'Connessione',   icon: 'solar:link-round-linear',   color: '#7CB8C9' },
  play:         { en: 'Play',         it: 'Gioco',         icon: 'solar:gamepad-linear',       color: '#E8B87E' },
  appreciation: { en: 'Appreciation', it: 'Apprezzamento', icon: 'solar:star-linear',          color: '#C4B58E' },
  presence:     { en: 'Presence',     it: 'Presenza',      icon: 'solar:eye-linear',           color: '#B8A9C9' },
  logistics:    { en: 'Logistics',    it: 'Logistica',     icon: 'solar:checklist-linear',     color: '#9BAFA3' },
  growth:       { en: 'Growth',       it: 'Crescita',      icon: 'solar:graph-up-linear',      color: '#A8C5A0' },
  husband:      { en: 'Husband',      it: 'Marito',        icon: 'solar:heart-linear',         color: '#E8A0B4' },
  father:       { en: 'Father',       it: 'Padre',         icon: 'solar:baby-linear',          color: '#D4A574' },
  partner:      { en: 'Partner',      it: 'Partner',       icon: 'solar:user-linear',          color: '#8FA8C8' },
};

/* ─── Species config for garden tree types ─── */
const SPECIES_CONFIG = [
  { key: 'treeA', label_en: 'Deciduous',  label_it: 'Latifoglia',  label_es: 'Caducifolio', mature: 'treeA_5',   tiles: ['treeA_1','treeA_2','treeA_3','treeA_4','treeA_5'] },
  { key: 'treeB', label_en: 'Broadleaf',  label_it: 'Sempreverde', label_es: 'Frondoso',    mature: 'treeB_5',   tiles: ['treeB_1','treeB_2','treeB_3','treeB_4','treeB_5'] },
  { key: 'treeC', label_en: 'Shrub',      label_it: 'Arbusto',     label_es: 'Arbusto',     mature: 'treeC_5',   tiles: ['treeC_1','treeC_2','treeC_3','treeC_4','treeC_5'] },
  { key: 'treeD', label_en: 'Palm',       label_it: 'Palma',       label_es: 'Palmera',     mature: 'treeD_5',   tiles: ['treeD_1','treeD_2','treeD_3','treeD_4','treeD_5'] },
  { key: 'oak',        label_en: 'Oak',        label_it: 'Quercia',  label_es: 'Roble',     mature: 'oak',        tiles: ['oak'] },
  { key: 'fruit_tree', label_en: 'Fruit Tree', label_it: 'Frutto',   label_es: 'Frutal',    mature: 'fruit_tree', tiles: ['fruit_tree'] },
  { key: 'palm',       label_en: 'Palm Tree',  label_it: 'Palma',    label_es: 'Palmera',   mature: 'palm',       tiles: ['palm'] },
  { key: 'cherry',     label_en: 'Cherry',     label_it: 'Ciliegio', label_es: 'Cerezo',    mature: 'cherry',     tiles: ['cherry'] },
  { key: 'pine',       label_en: 'Pine',       label_it: 'Pino',     label_es: 'Pino',      mature: 'pine',       tiles: ['pine'] },
];

/* ─── Tile preview — renders a single tile on a mini canvas ─── */
const TilePreview = React.memo(({ tileId, size = 48, spritesReady }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!spritesReady) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (tileRenderers[tileId]) {
      tileRenderers[tileId](ctx, 0, 76);
    }
  }, [tileId, spritesReady]);

  return (
    <canvas
      ref={canvasRef}
      width={80}
      height={86}
      style={{ width: size, height: size * 86 / 80, imageRendering: 'pixelated' }}
    />
  );
});

/* ─── SVG Donut Chart ─── */
const DonutChart = ({ data, size = 180 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 2;
  const innerR = outerR * 0.58;
  let cumulative = 0;

  const polarToCartesian = (cx, cy, r, angle) => ({
    x: cx + r * Math.cos(angle),
    y: cy + r * Math.sin(angle),
  });

  const slices = data.map((d, i) => {
    const startAngle = (cumulative / total) * 2 * Math.PI - Math.PI / 2;
    cumulative += d.value;
    const endAngle = (cumulative / total) * 2 * Math.PI - Math.PI / 2;
    const largeArc = d.value / total > 0.5 ? 1 : 0;
    const gap = data.length > 1 ? 0.02 : 0;

    const s1 = polarToCartesian(cx, cy, outerR, startAngle + gap);
    const e1 = polarToCartesian(cx, cy, outerR, endAngle - gap);
    const s2 = polarToCartesian(cx, cy, innerR, endAngle - gap);
    const e2 = polarToCartesian(cx, cy, innerR, startAngle + gap);

    const path = [
      `M ${s1.x} ${s1.y}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${e1.x} ${e1.y}`,
      `L ${s2.x} ${s2.y}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${e2.x} ${e2.y}`,
      'Z',
    ].join(' ');

    // percentage label position
    const midAngle = (startAngle + endAngle) / 2;
    const labelR = (outerR + innerR) / 2;
    const labelPos = polarToCartesian(cx, cy, labelR, midAngle);
    const pct = Math.round((d.value / total) * 100);

    return (
      <g key={i}>
        <path d={path} fill={d.color} />
        {pct >= 8 && (
          <text
            x={labelPos.x} y={labelPos.y}
            textAnchor="middle" dominantBaseline="central"
            style={{ fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 700, fill: '#fff' }}
          >
            {pct}%
          </text>
        )}
      </g>
    );
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices}
    </svg>
  );
};

const Stats = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user: contextUser, isPremium, isAdmin, isBetatester, token, loading: userLoading } = useUser();
  const { setHeaderProps } = useHeader();
  const { areaStats, refreshStats: refreshAreaStats } = useStats();
  const isIt = i18n.language === 'it';
  const isEs = i18n.language === 'es';
  const weekdays = isIt ? WEEKDAYS_IT : isEs ? WEEKDAYS_ES : WEEKDAYS_EN;

  const { gardenGrid } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState(7);
  const [selectedDay, setSelectedDay] = useState(null);
  const [spritesReady, setSpritesReady] = useState(false);

  // Load plant spritesheet for tile previews
  useEffect(() => {
    const img = new Image();
    img.src = plantsImgUrl;
    img.onload = () => { setPlantsImage(img); setSpritesReady(true); };
  }, []);

  const userId = contextUser?.user_id || contextUser?.id;
  const isFreeDebug = contextUser?.role === 'free-debug' || contextUser?.subscription_status === 'free-debug';
  const canSeeDetailedStats = isPremium || isAdmin || isBetatester || isFreeDebug;

  const handleNotificationClick = useCallback((action) => {
    if (action === 'suggestion' || action === 'checkin') navigate('/dashboard');
    else if (action === 'events') navigate('/events');
    else if (action === 'subscription') navigate('/subscription');
    else if (action === 'achievements') navigate('/achievements');
  }, [navigate]);

  /* ─── Fetch stats + metrics ─── */
  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || token;
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      const [statsRes, metricsRes] = await Promise.all([
        axios.get(`${API}/stats/${userId}?range=${selectedRange}`, { headers }),
        axios.get(`${API}/metrics/${userId}?period=${selectedRange <= 7 ? 'week' : selectedRange <= 30 ? 'month' : 'all'}`, { headers }),
      ]);
      setStats(statsRes.data);
      setMetrics(metricsRes.data);
    } catch (e) {
      console.error('[Stats] fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedRange, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { refreshAreaStats(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If user context finished loading but no userId yet, stop the spinner
  useEffect(() => {
    if (!userLoading && !userId) setLoading(false);
  }, [userLoading, userId]);

  useEffect(() => {
    setHeaderProps({
      title: t('stats.title'),
      user: contextUser,
      userId,
      onNotificationClick: handleNotificationClick,
    });
  }, [t, contextUser, userId, setHeaderProps, handleNotificationClick]);

  /* ─── Derived ─── */
  const streak = metrics?.current_streak || 0;
  const bestStreak = metrics?.best_streak || 0;
  const completionRate = metrics?.completion_rate || 0;
  const actionsCompleted = metrics?.actions_completed || 0;
  const totalMinutes = metrics?.total_minutes || 0;

  // Current-week dots (last 7 days from timeline)
  const weekDots = useMemo(() => {
    const timeline = stats?.timeline || [];
    const last7 = timeline.slice(-7);
    while (last7.length < 7) last7.unshift({ status: null });
    return last7;
  }, [stats]);

  // Category rows sorted by count
  const categoryRows = useMemo(() => {
    const raw = stats?.category_counts || metrics?.category_breakdown || {};
    const merged = {};
    for (const [key, val] of Object.entries(raw)) {
      const norm = ['partner', 'marito'].includes(key) ? 'husband' : key;
      merged[norm] = (merged[norm] || 0) + val;
    }
    return Object.entries(merged)
      .map(([key, count], idx) => ({
        key, count,
        ...(CATEGORY_CONFIG[key] || { en: key, it: key, icon: 'solar:circle-linear', color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length] }),
      }))
      .sort((a, b) => b.count - a.count);
  }, [stats, metrics]);

  const totalCategoryCount = categoryRows.reduce((sum, r) => sum + r.count, 0);

  // Donut chart data
  const donutData = useMemo(() =>
    categoryRows.map(r => ({ value: r.count, color: r.color, label: r.key })),
    [categoryRows]
  );

  const maxCategoryCount = categoryRows.length ? Math.max(...categoryRows.map(r => r.count), 1) : 1;

  // Species counts from garden grid
  const speciesCounts = useMemo(() => {
    if (!gardenGrid) return [];
    const counts = countTiles(gardenGrid);
    return SPECIES_CONFIG
      .map(spec => ({
        ...spec,
        count: spec.tiles.reduce((sum, t) => sum + (counts[t] || 0), 0),
      }))
      .filter(s => s.count > 0);
  }, [gardenGrid]);

  const totalTrees = speciesCounts.reduce((sum, s) => sum + s.count, 0);

  if (loading || userLoading || !stats) {
    return <StatsSkeleton />;
  }

  return (
    <div style={{ margin: 0, fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', padding: '0 0 40px' }}>

        {/* ═══════════════════════════════════════
            RANGE SELECTOR — pill tabs
            ═══════════════════════════════════════ */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 24px 0' }}>
          <div style={{
            display: 'inline-flex', background: 'var(--c-surface-card)',
            borderRadius: 999, padding: 3, width: '100%',
          }}>
            {[7, 30, 90].map((range) => (
              <button
                key={range}
                onClick={() => setSelectedRange(range)}
                style={{
                  flex: 1, padding: '8px 0', borderRadius: 999, border: 'none', cursor: 'pointer',
                  fontFamily: 'Manrope-Variable', fontSize: 12, fontWeight: 600,
                  transition: 'all 0.2s',
                  background: selectedRange === range ? 'var(--c-surface-elevated)' : 'transparent',
                  color: selectedRange === range ? 'var(--c-texPri)' : 'var(--c-texSec)',
                  boxShadow: selectedRange === range ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
                }}
              >
                {range === 7 ? (isIt ? 'Settimana' : isEs ? 'Semana' : 'Week')
                  : range === 30 ? (isIt ? 'Mese' : isEs ? 'Mes' : 'Month')
                  : (isIt ? 'Tutto' : isEs ? 'Todo' : 'All')}
              </button>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════
            OVERVIEW CARD — donut + legend
            ═══════════════════════════════════════ */}
        <div style={{
          margin: '16px 16px 0', borderRadius: 20, padding: '24px 20px',
          background: 'var(--c-surface-card)',
          border: '1px solid var(--c-border)',
        }}>
          {/* Donut chart */}
          {donutData.length > 0 && totalCategoryCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <div style={{ position: 'relative' }}>
                <DonutChart data={donutData} size={180} />
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)', textAlign: 'center',
                }}>
                  <div style={{
                    fontFamily: 'Manrope-Variable', fontSize: 28, fontWeight: 800,
                    color: 'var(--c-texPri)', lineHeight: 1,
                  }}>
                    {actionsCompleted}
                  </div>
                  <div style={{
                    fontFamily: 'Manrope-Variable', fontSize: 10, fontWeight: 500,
                    color: 'var(--c-texSec)', marginTop: 2,
                  }}>
                    {isIt ? 'gesti' : isEs ? 'gestos' : 'gestures'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Category legend rows */}
          {categoryRows.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {categoryRows.map(({ key, count, color, en, it, es }) => {
                const label = isIt ? it : isEs ? (es || en) : en;
                const pct = totalCategoryCount > 0 ? Math.round((count / totalCategoryCount) * 100) : 0;
                return (
                  <div key={key} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 0',
                    borderBottom: '1px solid var(--c-border)',
                  }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: color, flexShrink: 0,
                    }} />
                    <span style={{
                      fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 500,
                      color: 'var(--c-texPri)', flex: 1, textTransform: 'capitalize',
                    }}>
                      {label}
                    </span>
                    <span style={{
                      fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 600,
                      color: 'var(--c-texSec)', minWidth: 36, textAlign: 'right',
                    }}>
                      {pct}%
                    </span>
                    <span style={{
                      fontFamily: 'Manrope-Variable', fontSize: 13, fontWeight: 500,
                      color: 'var(--c-texTer)', minWidth: 20, textAlign: 'right',
                    }}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════
            STREAK + WEEK CARD
            ═══════════════════════════════════════ */}
        <div style={{
          margin: '12px 16px 0', borderRadius: 20, padding: '20px 20px',
          background: 'var(--c-surface-card)',
          border: '1px solid var(--c-border)',
        }}>
          {/* Streak hero row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #FF9500, #FF5E3A)',
              boxShadow: '0 4px 14px rgba(255,149,0,0.3), inset 0 -3px 0 rgba(0,0,0,0.18), inset 0 2px 0 rgba(255,255,255,0.3)',
              border: '3px solid #FF9500',
            }}>
              <Icon icon="solar:fire-bold" style={{ width: 22, height: 22, color: '#fff' }} />
            </div>
            <div>
              <div style={{
                fontFamily: 'Manrope-Variable', fontSize: 28, fontWeight: 800,
                color: 'var(--c-texPri)', lineHeight: 1, letterSpacing: '-0.02em',
              }}>
                {streak}
              </div>
              <div style={{
                fontFamily: 'Manrope-Variable', fontSize: 12, fontWeight: 500,
                color: 'var(--c-texSec)', marginTop: 2,
              }}>
                {streak === 1
                  ? (isIt ? 'giorno di fila' : isEs ? 'día de racha' : 'day streak')
                  : (isIt ? 'giorni di fila' : isEs ? 'días de racha' : 'day streak')}
              </div>
            </div>
            {bestStreak > 0 && (
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{
                  fontFamily: 'Manrope-Variable', fontSize: 18, fontWeight: 700,
                  color: 'var(--c-texPri)', lineHeight: 1,
                }}>
                  {bestStreak}
                </div>
                <div style={{
                  fontFamily: 'Manrope-Variable', fontSize: 10, fontWeight: 500,
                  color: 'var(--c-texSec)', marginTop: 2,
                }}>
                  {isIt ? 'record' : isEs ? 'récord' : 'best'}
                </div>
              </div>
            )}
          </div>

          {/* Week dots */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: 4,
            padding: '12px 0 4px',
            borderTop: '1px solid var(--c-border)',
          }}>
            {weekDots.map((day, idx) => (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1 }}>
                <span style={{
                  fontFamily: 'Manrope-Variable', fontSize: 10, fontWeight: 600,
                  color: 'var(--c-texTer)', textTransform: 'uppercase',
                }}>
                  {weekdays[idx]}
                </span>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: day.status === 'done'
                    ? 'linear-gradient(135deg, #7EA86C, #5C8748)'
                    : 'var(--c-surface-card)',
                  boxShadow: day.status === 'done'
                    ? '0 2px 10px rgba(126,168,108,0.18), inset 0 -2px 0 rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.2)'
                    : 'none',
                  border: day.status === 'done' ? '2px solid #7EA86C' : '2px solid transparent',
                }}>
                  {day.status === 'done' ? (
                    <Icon icon="solar:check-circle-bold" style={{ width: 14, height: 14, color: '#fff' }} />
                  ) : day.status === 'skipped' ? (
                    <Icon icon="solar:close-circle-linear" style={{ width: 12, height: 12, color: 'var(--c-texTer)' }} />
                  ) : (
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--c-texDis)' }} />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Motivational line */}
          <div style={{
            textAlign: 'center', fontFamily: 'Manrope-Variable', fontSize: 12,
            color: 'var(--c-texSec)', fontWeight: 400, paddingTop: 8,
          }}>
            {streak >= 7
              ? (isIt ? 'Costanza impressionante!' : isEs ? '¡Constancia impresionante!' : 'Impressive consistency!')
              : streak >= 3
                ? (isIt ? 'Stai costruendo un\'abitudine' : isEs ? 'Estás construyendo un hábito' : 'Building a habit')
                : streak >= 1
                  ? (isIt ? 'Continua così, ogni giorno conta' : isEs ? 'Sigue así, cada día cuenta' : 'Keep it up, every day counts')
                  : (isIt ? 'Inizia oggi, un gesto alla volta' : isEs ? 'Empieza hoy, un gesto a la vez' : 'Start today, one gesture at a time')}
          </div>
        </div>

        {/* ═══════════════════════════════════════
            QUICK STATS — horizontal row
            ═══════════════════════════════════════ */}
        <div style={{
          display: 'flex', gap: 8,
          margin: '12px 16px 0',
        }}>
          {[
            { label: isIt ? 'Tasso' : isEs ? 'Tasa' : 'Rate', value: `${completionRate}%`, icon: 'solar:chart-2-bold' },
            { label: isIt ? 'Minuti' : isEs ? 'Minutos' : 'Minutes', value: totalMinutes, icon: 'solar:clock-circle-bold' },
          ].map(({ label, value, icon }) => (
            <div key={label} style={{
              flex: 1, borderRadius: 16, padding: '16px 14px',
              background: 'var(--c-surface-card)',
              border: '1px solid var(--c-border)',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #7EA86C, #5C8748)',
                boxShadow: '0 3px 12px rgba(126,168,108,0.18), inset 0 -2px 0 rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.2)',
                border: '2px solid #7EA86C',
                marginBottom: 10,
              }}>
                <Icon icon={icon} style={{ width: 16, height: 16, color: '#fff' }} />
              </div>
              <div style={{
                fontFamily: 'Manrope-Variable', fontSize: 22, fontWeight: 800,
                color: 'var(--c-texPri)', lineHeight: 1,
              }}>
                {value}
              </div>
              <div style={{
                fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 500,
                color: 'var(--c-texSec)', marginTop: 4,
              }}>
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* ═══════════════════════════════════════
            TENSION AREA PROGRESS — premium only
            ═══════════════════════════════════════ */}
        {canSeeDetailedStats && areaStats?.has_data && areaStats?.areas?.length > 0 && (
          <div style={{
            margin: '12px 16px 0', borderRadius: 20, padding: '20px 20px',
            background: 'var(--c-surface-card)',
            border: '1px solid var(--c-border)',
          }}>
            <div style={{
              fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 700,
              color: 'var(--c-texPri)', marginBottom: 14,
            }}>
              {isIt ? 'Aree di tensione' : isEs ? 'Áreas de tensión' : 'Tension areas'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {areaStats.areas.map((area) => {
                const label = isIt ? area.label_it : isEs ? (area.label_es || area.label_en) : area.label_en;
                return (
                  <div key={area.key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <span style={{ fontFamily: 'Manrope-Variable', fontSize: 13, fontWeight: 500, color: 'var(--c-texPri)' }}>
                        {label}
                      </span>
                      <span style={{ fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 600, color: 'var(--c-texSec)' }}>
                        {area.completed}/{area.total}
                      </span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--c-border)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 3,
                        width: `${area.completion_rate}%`,
                        background: 'linear-gradient(90deg, #7EA86C, #5C8748)',
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}



        {/* ═══════════════════════════════════════
            SPECIES PLANTED — garden tree breakdown
            ═══════════════════════════════════════ */}
        {speciesCounts.length > 0 && (
          <div style={{
            margin: '12px 16px 0', borderRadius: 20, padding: '20px 20px',
            background: 'var(--c-surface-card)',
            border: '1px solid var(--c-border)',
          }}>
            <div style={{
              fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 700,
              color: 'var(--c-texPri)', marginBottom: 4,
            }}>
              {isIt ? 'Specie piantate' : isEs ? 'Especies plantadas' : 'Species planted'}
            </div>
            <div style={{
              fontFamily: 'Manrope-Variable', fontSize: 12, fontWeight: 500,
              color: 'var(--c-texSec)', marginBottom: 16,
            }}>
              {totalTrees} {isIt ? 'alberi nel giardino' : isEs ? 'árboles en el jardín' : 'trees in the garden'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {speciesCounts.map(({ key, label_en, label_it, label_es, mature, count }) => (
                <div key={key} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  padding: '12px 4px 10px',
                  borderRadius: 14,
                  background: 'var(--c-surface-card)',
                }}>
                  <TilePreview tileId={mature} size={44} spritesReady={spritesReady} />
                  <div style={{
                    fontFamily: 'Manrope-Variable', fontSize: 18, fontWeight: 800,
                    color: 'var(--c-texPri)', lineHeight: 1, marginTop: 6,
                  }}>
                    {count}
                  </div>
                  <div style={{
                    fontFamily: 'Manrope-Variable', fontSize: 10, fontWeight: 500,
                    color: 'var(--c-texSec)', marginTop: 2, textAlign: 'center',
                  }}>
                    {isIt ? label_it : isEs ? (label_es || label_en) : label_en}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════
            GESTURE DETAIL MODAL
            ═══════════════════════════════════════ */}
        {selectedDay && (
          <div
            onClick={() => setSelectedDay(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.45)', display: 'flex',
              alignItems: 'flex-end', justifyContent: 'center',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 'clamp(390px, 70vw, 560px)', background: '#1a2e1a',
                borderRadius: '24px 24px 0 0', padding: '28px 24px 36px',
                boxShadow: '0 -8px 30px rgba(0,0,0,0.3)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderBottom: 'none',
              }}
            >
              {/* drag handle */}
              <div style={{
                width: 36, height: 4, borderRadius: 2,
                background: 'rgba(255,255,255,0.2)', margin: '0 auto 20px',
              }} />
              <div style={{
                fontFamily: 'Manrope-Variable', fontSize: 12, fontWeight: 500,
                color: 'rgba(255,255,255,0.5)', marginBottom: 6,
              }}>
                {selectedDay.date}
              </div>
              {selectedDay.category && (
                <div style={{
                  display: 'inline-block', fontFamily: 'Manrope-Variable', fontSize: 11,
                  fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginBottom: 10,
                  padding: '3px 10px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.1)',
                }}>
                  {t(`dashboard.category.${selectedDay.category}`, { defaultValue: selectedDay.category })}
                </div>
              )}
              <div style={{
                fontFamily: 'Manrope-Variable', fontSize: 16, fontWeight: 600,
                color: '#fff', lineHeight: 1.5,
              }}>
                {selectedDay.gesture_text}
              </div>
              <button
                onClick={() => setSelectedDay(null)}
                style={{
                  marginTop: 24, width: '100%', padding: '14px 0',
                  border: 'none', borderRadius: 14, cursor: 'pointer',
                  fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 600,
                  background: 'rgba(255,255,255,0.1)', color: '#fff',
                }}
              >
                {isIt ? 'Chiudi' : isEs ? 'Cerrar' : 'Close'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Stats;
